import { useMemo } from 'react';

import {
	AppointmentNamesToReasonForVisitTypes,
	AppointmentName,
} from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { Appointment } from '@src/components/UserPortal/UserPortal.types';
import { useAppointmentsStore, useClientInformationStore, usePetsInformationStore } from '@src/store';
import { ReasonForVisitType } from '@src/store/modules/reason-for-visit.store';

export type ConflictMap = Partial<Record<ReasonForVisitType, Appointment>>;

function fillConflictMap(
	result: ConflictMap,
	appointment: Appointment,
	reasonMapping: ReasonForVisitType | ReasonForVisitType[]
): void {
	const reasonTypes = Array.isArray(reasonMapping) ? reasonMapping : [reasonMapping];
	for (const reasonType of reasonTypes) {
		result[reasonType] ??= appointment;
	}
}

export function useAppointmentConflict(): ConflictMap {
	const { clientId } = useClientInformationStore((store) => store);
	const { selectedPet } = usePetsInformationStore((store) => store);
	const { getAppointmentByPatientId } = useAppointmentsStore((store) => store);

	return useMemo(() => {
		if (!clientId || !selectedPet) return {};

		const appointments = getAppointmentByPatientId(selectedPet);
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const result: ConflictMap = {};

		for (const appointment of appointments) {
			if (appointment.status === 'CANCELLED') continue;

			const appointmentDate = new Date(appointment.start);
			appointmentDate.setHours(0, 0, 0, 0);
			if (appointmentDate < today) continue;

			const typeName = appointment.type?.name;
			if (!typeName) continue;

			const reasonMapping = AppointmentNamesToReasonForVisitTypes[typeName as AppointmentName];
			if (!reasonMapping) continue;

			fillConflictMap(result, appointment, reasonMapping);
		}

		return result;
	}, [clientId, selectedPet, getAppointmentByPatientId]);
}
